import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { portalAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Activity, Calendar, Dumbbell, Check, Clock, Heart, Send,
} from "lucide-react";
import { toast } from "sonner";

export default function PatientPortalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [painLevel, setPainLevel] = useState(5);
  const [checkinNotes, setCheckinNotes] = useState("");
  const [completedExercises, setCompletedExercises] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Link invalido. Solicita un nuevo link a tu fisioterapeuta.");
      setLoading(false);
      return;
    }
    portalAPI.info(token)
      .then((res) => setData(res.data))
      .catch(() => setError("Link expirado o invalido. Solicita un nuevo link."))
      .finally(() => setLoading(false));
  }, [token]);

  const confirmAppointment = async (apptId) => {
    try {
      await portalAPI.confirmAppointment({ token, appointment_id: apptId });
      toast.success("Cita confirmada");
      const res = await portalAPI.info(token);
      setData(res.data);
    } catch {
      toast.error("Error al confirmar");
    }
  };

  const submitCheckin = async () => {
    setSubmitting(true);
    try {
      await portalAPI.checkin({
        token,
        pain_level: painLevel,
        notes: checkinNotes,
        exercises_completed: completedExercises,
      });
      toast.success("Check-in enviado. Tu fisioterapeuta lo revisara.");
      setCheckinNotes("");
      setPainLevel(5);
      setCompletedExercises([]);
    } catch {
      toast.error("Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExercise = (name) => {
    setCompletedExercises((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-sm w-full text-center">
          <CardContent className="p-8">
            <Activity className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { patient, tenant, next_appointment, exercise_plans, active_episodes } = data || {};
  // Fase 1c: prefiere los items resueltos de plan_items; cae al JSON exercises (planes legacy).
  const allExercises = exercise_plans?.flatMap((p) => {
    const fromItems = (p.items || []).map((it) => ({
      name: it.exercise?.name || "Ejercicio",
      description: it.exercise?.description || "",
      sets: it.sets,
      reps: it.reps,
      frequency: it.frequency,
      planTitle: p.title,
    }));
    if (fromItems.length) return fromItems;
    return (p.exercises || []).map((e) => ({ ...e, planTitle: p.title }));
  }) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="patient-portal">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-4 py-6">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5" />
            <span className="text-sm font-semibold">{tenant?.name || "Fisiotherapy.app"}</span>
          </div>
          <h1 className="text-xl font-bold">Hola, {patient?.name?.split(" ")[0]}</h1>
          <p className="text-sm opacity-80 mt-0.5">Tu portal de fisioterapia</p>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4 -mt-2">
        {/* Next Appointment */}
        <Card className="shadow-md" data-testid="portal-next-appointment">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" />Proxima cita</CardTitle>
          </CardHeader>
          <CardContent>
            {next_appointment ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="text-center bg-primary/5 rounded-xl px-4 py-2">
                    <p className="text-lg font-bold text-primary">{next_appointment.date?.split("-")[2]}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      {new Date(next_appointment.date + "T12:00").toLocaleDateString("es-MX", { month: "short" })}
                    </p>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium">{next_appointment.start_time} - {next_appointment.end_time}</span>
                    </div>
                    <Badge
                      variant="secondary"
                      className={`mt-1 text-[10px] ${
                        next_appointment.status === "confirmed"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                      }`}
                    >
                      {next_appointment.status === "confirmed" ? "Confirmada" : "Pendiente de confirmar"}
                    </Badge>
                  </div>
                </div>
                {next_appointment.status !== "confirmed" && (
                  <Button
                    className="w-full rounded-full"
                    onClick={() => confirmAppointment(next_appointment.id)}
                    data-testid="confirm-appointment-btn"
                  >
                    <Check className="w-4 h-4 mr-1.5" />Confirmar cita
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No hay citas proximas programadas</p>
            )}
          </CardContent>
        </Card>

        {/* Exercises */}
        {allExercises.length > 0 && (
          <Card className="shadow-md" data-testid="portal-exercises">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Dumbbell className="w-4 h-4 text-primary" />Mis ejercicios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {allExercises.map((ex, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                    completedExercises.includes(ex.name)
                      ? "bg-primary/5 border-primary/30"
                      : "bg-muted/30 border-border"
                  }`}
                  onClick={() => toggleExercise(ex.name)}
                  data-testid={`portal-exercise-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={completedExercises.includes(ex.name)}
                      onCheckedChange={() => toggleExercise(ex.name)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${completedExercises.includes(ex.name) ? "line-through text-muted-foreground" : "text-foreground"}`}>
                        {ex.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ex.sets} series x {ex.reps} reps &middot; {ex.frequency}
                      </p>
                    </div>
                  </div>
                  {ex.description && <p className="text-xs text-muted-foreground mt-1.5 ml-9">{ex.description}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Check-in */}
        <Card className="shadow-md" data-testid="portal-checkin">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-secondary" />Check-in de hoy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Como esta tu dolor hoy? {painLevel}/10</Label>
              <Slider
                value={[painLevel]}
                onValueChange={([v]) => setPainLevel(v)}
                min={0} max={10} step={1}
                className="mt-2"
                data-testid="portal-pain-slider"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>Sin dolor</span><span>Dolor maximo</span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notas o comentarios</Label>
              <Textarea
                value={checkinNotes}
                onChange={(e) => setCheckinNotes(e.target.value)}
                rows={2}
                placeholder="Como te sientes hoy?"
                data-testid="portal-checkin-notes"
              />
            </div>
            <Button
              className="w-full rounded-full"
              onClick={submitCheckin}
              disabled={submitting}
              data-testid="portal-checkin-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {submitting ? "Enviando..." : "Enviar check-in"}
            </Button>
          </CardContent>
        </Card>

        {/* Active Episodes */}
        {active_episodes?.length > 0 && (
          <Card className="shadow-md" data-testid="portal-episodes">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-primary" />Mi tratamiento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {active_episodes.map((ep) => (
                <div key={ep.id} className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">{ep.title}</p>
                  {ep.chief_complaint && <p className="text-xs text-muted-foreground mt-0.5">{ep.chief_complaint}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-[10px] text-muted-foreground py-4">
          Fisiotherapy.app &middot; {tenant?.name}
        </p>
      </div>
    </div>
  );
}
